import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-page min-h-screen" data-testid="privacy-policy-page">
      <Helmet>
        <title>Політика конфіденційності - CIMM Computers</title>
        <meta name="description" content="Політика конфіденційності CIMM Computers. Як ми збираємо, використовуємо та захищаємо ваші персональні дані." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Header */}
      <header className="cyber-header fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="text-2xl font-bold neon-text">CIMM</div>
            <div className="text-sm text-gray-400 hidden md:block">Комп'ютери та Ремонт</div>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-gray-300 hover:text-orange-400/80 transition-all duration-300">Головна</Link>
            <Link to="/phones" className="text-gray-300 hover:text-orange-400/80 transition-all duration-300">Телефони</Link>
            <Link to="/laptops" className="text-gray-300 hover:text-orange-400/80 transition-all duration-300">Ноутбуки</Link>
            <Link to="/contacts" className="text-gray-300 hover:text-orange-400/80 transition-all duration-300">Контакти</Link>
          </nav>
          <a href="tel:+380974178788" className="neon-text font-semibold hover:drop-shadow-[0_0_15px_rgba(255,140,0,0.8)] transition-all duration-300">
            +38 097 417 8788
          </a>
        </div>
      </header>

      {/* Content */}
      <section className="pt-32 pb-16 px-4 dark-section">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-orange-500 icon-glow" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Політика <span className="neon-text">конфіденційності</span>
            </h1>
            <p className="text-gray-400">Останнє оновлення: 2026 рік</p>
          </div>

          <div className="glass-card p-8 space-y-8 text-gray-300 leading-relaxed" data-testid="privacy-policy-content">
            <div>
              <h2 className="text-2xl font-bold text-white mb-3">1. Загальні положення</h2>
              <p>
                Ця Політика конфіденційності визначає порядок обробки та захисту персональних даних користувачів
                сайту cimm.od.ua (далі — «Сайт»), який належить сервісному центру CIMM Computers
                (далі — «ми», «Компанія»). Використовуючи Сайт та надсилаючи свої дані через форми зворотного
                зв'язку, ви погоджуєтесь з умовами цієї Політики.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">2. Які дані ми збираємо</h2>
              <p className="mb-2">Ми можемо збирати наступні персональні дані, які ви добровільно надаєте:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Ім'я;</li>
                <li>Номер телефону;</li>
                <li>Адреса електронної пошти;</li>
                <li>Інформація про пристрій та опис проблеми, яку ви надсилаєте у заявці.</li>
              </ul>
              <p className="mt-2">
                Також автоматично можуть збиратися знеособлені технічні дані (тип браузера, файли cookie,
                дані аналітики Google) для покращення роботи Сайту.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">3. Мета обробки даних</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Зв'язок з вами для обробки заявки на ремонт або консультацію;</li>
                <li>Надання послуг сервісного центру;</li>
                <li>Інформування про статус замовлення;</li>
                <li>Покращення якості обслуговування та роботи Сайту.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">4. Захист та зберігання даних</h2>
              <p>
                Ми вживаємо необхідних організаційних і технічних заходів для захисту ваших персональних даних
                від несанкціонованого доступу, зміни, розкриття чи знищення. Дані зберігаються лише протягом
                строку, необхідного для досягнення вказаних цілей.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">5. Передача даних третім особам</h2>
              <p>
                Ми не продаємо та не передаємо ваші персональні дані третім особам, окрім випадків, передбачених
                законодавством України, або коли це необхідно для надання послуги (наприклад, служби доставки).
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">6. Файли cookie</h2>
              <p>
                Сайт використовує файли cookie та інструменти веб-аналітики (Google Analytics / Google Tag Manager)
                для збору знеособленої статистики відвідувань. Ви можете вимкнути cookie у налаштуваннях вашого браузера.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">7. Ваші права</h2>
              <p>
                Ви маєте право отримати інформацію про обробку ваших даних, вимагати їх виправлення або видалення,
                а також відкликати згоду на обробку. Для цього зв'яжіться з нами за вказаними нижче контактами.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-white mb-3">8. Контакти</h2>
              <p>З усіх питань щодо обробки персональних даних звертайтеся:</p>
              <ul className="list-none mt-2 space-y-1">
                <li>Сервісний центр <span className="text-white font-semibold">CIMM Computers</span></li>
                <li>Адреса: <span className="text-white">м. Одеса, вул. Одеська, 45</span></li>
                <li>Телефон: <a href="tel:+380974178788" className="text-orange-500 hover:text-orange-400">+38 097 417 8788</a></li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/" className="text-orange-500 hover:text-orange-400 transition-colors" data-testid="privacy-back-home">
              ← Повернутися на головну
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="cyber-footer py-12 px-4">
        <div className="container mx-auto">
          <div className="border-t border-orange-500/20 pt-8 text-center text-gray-400">
            <p>© 2026 CIMM Computers. Всі права захищені.</p>
            <p className="text-xs text-gray-500 mt-3 max-w-3xl mx-auto">Незалежний сервісний центр з постгарантійного обслуговування. Ми не є офіційним (авторизованим) сервісним центром і не пов'язані з виробниками техніки (Apple, Samsung, Sony, Xiaomi та ін.). Усі назви брендів належать їхнім власникам.</p>
            <p className="mt-2">
              <Link to="/privacy-policy" className="hover:text-orange-500 transition-colors">Політика конфіденційності</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
